import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { type Goal, GoalCategory } from "web_myplann/Goals/Types/GoalTypes";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { z } from "zod";

const getGoalFormSchema = (t: (key: string) => string) =>
	z.object({
		category: z.nativeEnum(GoalCategory, {
			required_error: t(Resources.Form.Validations.required),
		}),
		name: z
			.string({
				required_error: t(Resources.Form.Validations.required),
			})
			.min(1, t(Resources.Form.Validations.required)),
		dueDate: z.date({
			required_error: t(Resources.Form.Validations.required),
			invalid_type_error: t(Resources.Form.Validations.required),
		}),
		totalValue: z
			.number({
				required_error: t(Resources.Form.Validations.required),
				invalid_type_error: t(Resources.Form.Validations.required),
			})
			.positive(),
		ownFunds: z.number().min(0),
		loanAmount: z.number().min(0),
		note: z.string().optional(),
	});

export type GoalFormModel = z.infer<ReturnType<typeof getGoalFormSchema>>;

type UseGoalFormProps = {
	goal?: Goal;
	initialCategory?: GoalCategory;
};

const getDefaultDueDate = () => {
	const now = new Date();
	return new Date(now.getFullYear() + 5, now.getMonth(), 1);
};

const goalToFormValues = (
	goal?: Goal,
	initialCategory?: GoalCategory,
): Partial<GoalFormModel> => ({
	category: goal?.category ?? initialCategory,
	name: goal?.name ?? "",
	dueDate: getDefaultDueDate(),
	totalValue: goal?.totalValue,
	ownFunds: 0,
	loanAmount: 0,
	note: "",
});

export const useGoalForm = ({ goal, initialCategory }: UseGoalFormProps) => {
	const { t } = useResource();

	return useForm<GoalFormModel>({
		resolver: zodResolver(getGoalFormSchema(t)),
		defaultValues: goalToFormValues(goal, initialCategory),
	});
};
